// The in-cell editors. Each one owns its keys while open: Enter saves and moves down, Tab /
// Shift+Tab save and move right / left, Esc cancels. A list's choices float under the cell.

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { FocusEvent, KeyboardEvent, ReactNode, RefObject } from "react";
import { createPortal } from "react-dom";
import {
  PRODUCTION_NOTE_SUGGESTIONS,
  TIME_HALVES,
  TIME_HOURS,
  TIME_MINUTES,
  halfShown,
  hour12,
  hour24,
  isDay,
  localToday,
  momentAt,
  momentText,
  joinValues,
  rankListValues,
  splitValues,
  valuesText,
  pickableStatuses,
  takesAsap,
  timeOf,
  timePick,
  timeParts,
  timeShown,
} from "@app/shared";
import type { DateCell, ListValueRecord, TimeHalf, TimePick } from "@app/shared";
import { cn } from "@/lib/utils";
import type { ColumnDef } from "./columns";
import { floatBox } from "./float-box";
import { CalendarPanel, dayOfText, typedDay } from "./pickers";

export type Move = "down" | "right" | "left" | null;

export interface EditorProps {
  column: ColumnDef;
  /** The cell's value now (stored form: YYYY-MM-DD, ASAP, text). */
  value: string;
  /** A typed character that opened the editor, replacing the value. */
  initial?: string;
  anchor: RefObject<HTMLElement | null>;
  listValues: readonly ListValueRecord[];
  usage: ReadonlyMap<string, number>;
  /** Save. Resolves false when the value was refused (the editor stays open). */
  commit: (value: string, move: Move) => void;
  cancel: () => void;
  /** A message is open over the cell: its calendar or list is not shown (it would hide the message's buttons). */
  quiet?: boolean;
  /** List cells: "Add “X” as a new value…"; Work Needed: saved beside the values ticked (`alongWith`). */
  addValue: (typed: string, move: Move, alongWith?: readonly string[]) => void;
}

const MAX_SHOWN = 12;

// OUTSIDE-PLATFORM: P7 Popover and Combobox cannot anchor to a grid cell and leave focus in it.
/**
 * A panel fixed under the cell, outside the grid's scroll box so nothing clips it; inside the window,
 * ending at the cell's right edge when it would pass the window's (`floatBox`).
 */
export function Floating({
  anchor,
  children,
  width,
}: {
  anchor: RefObject<HTMLElement | null>;
  children: ReactNode;
  width?: number;
}) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  useLayoutEffect(() => {
    const update = () => setRect(anchor.current?.getBoundingClientRect() ?? null);
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [anchor]);
  if (!rect) return null;
  const below = window.innerHeight - rect.bottom > 260 || rect.top < 260;
  const box = floatBox(rect, width, window.innerWidth);
  return createPortal(
    <div
      className="bg-popover text-popover-foreground ring-foreground/10 fixed z-50 overflow-hidden rounded-md text-[13px] shadow-lg ring-1"
      style={{
        left: box.left,
        width: box.width,
        ...(below ? { top: rect.bottom + 2 } : { bottom: window.innerHeight - rect.top + 2 }),
      }}
      // Keep focus in the cell's input: a click in the panel must not blur it first.
      onMouseDown={(e) => e.preventDefault()}
      // The keyboard's way into it (a calendar's days): not leaving the cell (`leftCell`).
      data-cell-panel=""
    >
      {children}
    </div>,
    document.body,
  );
}

function keyMove(e: KeyboardEvent): Move | "cancel" | undefined {
  if (e.key === "Escape") return "cancel";
  if (e.key === "Enter") return "down";
  if (e.key === "Tab") return e.shiftKey ? "left" : "right";
  return undefined;
}

function useSelectAll(ref: RefObject<HTMLInputElement | null>, initial: string | undefined) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    if (initial === undefined) el.select();
    else el.setSelectionRange(el.value.length, el.value.length);
  }, [ref, initial]);
}

// Leaving the cell saves. Leaving the window does not (as in Excel), nor does a message or dialog
// opening over the cell: it would save the refused value again and queue the same message.
export const leftCell = (e: FocusEvent) =>
  document.hasFocus() &&
  !(
    e.relatedTarget instanceof Element && e.relatedTarget.closest("[role=alertdialog],[role=dialog],[data-cell-panel]")
  );

const inputClass = "h-full w-full bg-background px-2 text-[13px] outline-none";

export function TextEditor({ value, initial, commit, cancel, column }: EditorProps) {
  const ref = useRef<HTMLInputElement>(null);
  const [text, setText] = useState(initial ?? value);
  useSelectAll(ref, initial);
  return (
    <input
      ref={ref}
      aria-label={column.header}
      className={inputClass}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={(e) => leftCell(e) && commit(text, null)}
      onKeyDown={(e) => {
        const m = keyMove(e);
        if (m === undefined) return;
        e.preventDefault();
        e.stopPropagation();
        if (m === "cancel") cancel();
        else commit(text, m);
      }}
    />
  );
}

/**
 * "10/15", "10/15/26" or ASAP, and the calendar under the cell however the editor opened (Enter,
 * a double-click, typing): Alt+↓ takes the keyboard into it, Escape there comes back.
 */
export function DateEditor({ value, initial, commit, cancel, column, anchor, quiet }: EditorProps) {
  const ref = useRef<HTMLInputElement>(null);
  const shownValue = isDay(value) ? `${value.slice(5, 7)}/${value.slice(8, 10)}/${value.slice(2, 4)}` : value;
  const [text, setText] = useState(initial ?? shownValue);
  const [keys, setKeys] = useState(false);
  useSelectAll(ref, initial);
  const today = localToday();
  return (
    <>
      <input
        ref={ref}
        aria-label={column.header}
        placeholder={takesAsap(column.field as DateCell) ? "MM/DD or ASAP" : "MM/DD"}
        className={inputClass}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={(e) => leftCell(e) && commit(text, null)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" && e.altKey) {
            e.preventDefault();
            e.stopPropagation();
            setKeys(true);
            return;
          }
          const m = keyMove(e);
          if (m === undefined) return;
          e.preventDefault();
          e.stopPropagation();
          if (m === "cancel") cancel();
          else commit(text, m);
        }}
      />
      {quiet ? null : (
        <Floating anchor={anchor} width={290}>
          <CalendarPanel
            key={keys ? "keys" : "mouse"}
            selected={dayOfText(text, today)}
            today={today}
            autoFocus={keys}
            onPick={(d) => commit(typedDay(d), null)}
            onClear={() => commit("", null)}
            onEscape={() => {
              setKeys(false);
              ref.current?.focus();
            }}
          />
          {takesAsap(column.field as DateCell) ? (
            <button
              type="button"
              className="hover:bg-muted w-full border-t px-3 py-1.5 text-left font-medium"
              onClick={() => commit("ASAP", null)}
            >
              ASAP
            </button>
          ) : null}
        </Floating>
      )}
    </>
  );
}

const pad2 = (n: number) => String(n).padStart(2, "0");

/**
 * A time (In:, Out:, App Time): typed ("7:42 am", "2:15 pm", "14:30", "1425", read by `parseTime`
 * where it is saved) or picked under the cell — hours 12, 1–11 with an AM / PM switch (at first the
 * time's own half, else the half of the day it is now), minutes 00–55 in 5-minute steps, Now (the
 * current half), Clear. Shown h:mm AM / PM (`timeShown`); the value saved is the text, so every save
 * goes through the cell's own checks. A picked hour or half waits; a picked minute, Now, Clear or
 * Enter saves. The keyboard stays in the input: ↑ ↓ step the list in use (the hours first, across
 * noon and midnight), ← → switch lists once one is in use, typing goes back to the text. Esc
 * cancels; leaving the cell saves, as `DateEditor`.
 */
export function TimeEditor({
  value,
  initial,
  label,
  commit,
  cancel,
  anchor,
  inputRef,
  quiet,
  className,
}: {
  /** The stored time (H:MM) or "". */
  value: string;
  /** A typed character that opened the editor, replacing the value. */
  initial?: string;
  label: string;
  /** Save the text as typed or picked: the caller parses and checks it. */
  commit: (value: string, move: Move) => void;
  cancel: () => void;
  /** What the lists float under: the cell; the input when absent. */
  anchor?: RefObject<HTMLElement | null>;
  /** The input, for a caller that puts the focus back after a refused value. */
  inputRef?: RefObject<HTMLInputElement | null>;
  quiet?: boolean;
  className?: string;
}) {
  const ownRef = useRef<HTMLInputElement>(null);
  const ref = inputRef ?? ownRef;
  const [text, setText] = useState(initial ?? timeShown(value));
  /** The list ↑ ↓ step; null while typing. */
  const [list, setList] = useState<"hh" | "mm" | null>(null);
  useSelectAll(ref, initial);
  const parts = timeParts(text);
  const nowHour = new Date().getHours();
  // The AM / PM switch: the time's own half while there is one, else the one picked, else now's.
  const [halfPicked, setHalfPicked] = useState<TimeHalf | null>(null);
  const half = parts ? halfShown(text, new Date()) : (halfPicked ?? halfShown("", new Date()));
  const show = (h: number, m: number) => setText(timeOf(h, m));
  // What a pick shows is what it saves: refused, the editor keeps it (hour, minute, half and the text), as typed.
  const pick = (p: TimePick) => {
    if (typeof p === "object" && "half" in p) setHalfPicked(p.half);
    const r = timePick(text, p, new Date(), half);
    setText(r.text);
    if (r.save) commit(r.text, null);
  };
  // The minute's 5-minute slot: a typed 7:42 highlights 40.
  const slot = parts ? Math.floor(parts.m / 5) * 5 : null;
  const step = (which: "hh" | "mm", by: 1 | -1) => {
    if (which === "hh") {
      const h = parts ? (parts.h + by + 24) % 24 : nowHour;
      show(h, parts?.m ?? 0);
    } else {
      const i = slot === null ? 0 : (TIME_MINUTES.indexOf(slot) + by + TIME_MINUTES.length) % TIME_MINUTES.length;
      show(parts?.h ?? nowHour, TIME_MINUTES[i]!);
    }
  };
  return (
    <>
      <input
        ref={ref}
        aria-label={label}
        placeholder="h:mm AM/PM"
        className={className ?? inputClass}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setList(null);
        }}
        onBlur={(e) => leftCell(e) && commit(text, null)}
        onKeyDown={(e) => {
          if (e.key === "ArrowUp" || e.key === "ArrowDown") {
            e.preventDefault();
            e.stopPropagation();
            const which = list ?? "hh";
            setList(which);
            step(which, e.key === "ArrowDown" ? 1 : -1);
            return;
          }
          if (list && (e.key === "ArrowLeft" || e.key === "ArrowRight")) {
            e.preventDefault();
            e.stopPropagation();
            setList(e.key === "ArrowLeft" ? "hh" : "mm");
            return;
          }
          const m = keyMove(e);
          if (m === undefined) return;
          e.preventDefault();
          e.stopPropagation();
          if (m === "cancel") cancel();
          else commit(text, m);
        }}
      />
      {quiet ? null : (
        <Floating anchor={anchor ?? ref} width={176}>
          <HalfSwitch half={half} onPick={(h) => pick({ half: h })} />
          <div className="flex" data-testid="time-picker">
            <TimeList
              label="Hour"
              items={TIME_HOURS}
              format={String}
              selected={parts ? hour12(parts.h) : null}
              scrollTo={hour12(parts?.h ?? nowHour)}
              active={list === "hh"}
              onPick={(h) => {
                setList("hh");
                pick({ hour: h });
              }}
            />
            <TimeList
              label="Min"
              items={TIME_MINUTES}
              selected={slot}
              scrollTo={slot ?? 0}
              active={list === "mm"}
              onPick={(m) => pick({ minute: m })}
            />
          </div>
          <div className="flex items-center justify-between border-t px-3 py-1.5 text-xs">
            <button type="button" className="text-primary font-medium hover:underline" onClick={() => pick("now")}>
              Now
            </button>
            <button type="button" className="text-primary hover:underline" onClick={() => pick("clear")}>
              Clear
            </button>
          </div>
        </Floating>
      )}
    </>
  );
}

/** A moment's text split in its date ("10/30/26") and its time ("2:05 PM", "14:05"). */
const momentTextParts = (text: string) => {
  const [day = "", ...time] = text.trim().split(/\s+/);
  return { day, time: time.join(" ") };
};

/**
 * A day and a time (PE Order Start Time, End Time): typed "10/30 2:05 PM" (or "10/30 14:05"), or
 * picked under the cell — the calendar beside the hours (12, 1–11), the minutes and AM / PM (as
 * `DateEditor` and `TimeEditor`), Now and Clear. A picked day, hour or half waits; a picked minute
 * (with a day), Now, Clear or Enter saves the text, which the cell parses and checks. Esc cancels;
 * leaving the cell saves.
 */
export function MomentEditor({ value, initial, commit, cancel, column, anchor, quiet }: EditorProps) {
  const ref = useRef<HTMLInputElement>(null);
  const [text, setText] = useState(initial ?? momentText(value));
  useSelectAll(ref, initial);
  const today = localToday();
  const { day, time } = momentTextParts(text);
  const parts = timeParts(time);
  const slot = parts ? Math.floor(parts.m / 5) * 5 : null;
  const nowHour = new Date().getHours();
  const [halfPicked, setHalfPicked] = useState<TimeHalf | null>(null);
  const half = parts ? halfShown(time, new Date()) : (halfPicked ?? halfShown("", new Date()));
  const dayOr = (d: string) => d || typedDay(dayOfText(today, today) ?? new Date());
  const set = (d: string, t: string) => setText(`${d} ${t}`.trim());
  return (
    <>
      <input
        ref={ref}
        aria-label={column.header}
        placeholder="MM/DD h:mm AM/PM"
        className={inputClass}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={(e) => leftCell(e) && commit(text, null)}
        onKeyDown={(e) => {
          const m = keyMove(e);
          if (m === undefined) return;
          e.preventDefault();
          e.stopPropagation();
          if (m === "cancel") cancel();
          else commit(text, m);
        }}
      />
      {quiet ? null : (
        <Floating anchor={anchor} width={466}>
          <div className="flex" data-testid="moment-picker">
            <div className="w-[290px] border-r">
              <CalendarPanel
                selected={dayOfText(day, today)}
                today={today}
                onPick={(d) => {
                  set(typedDay(d), time);
                  ref.current?.focus();
                }}
              />
            </div>
            <div className="flex flex-1 flex-col">
              <HalfSwitch
                half={half}
                onPick={(h) => {
                  setHalfPicked(h);
                  if (parts) set(dayOr(day), timeOf(hour24(hour12(parts.h), h), parts.m));
                }}
              />
              <div className="flex">
                <TimeList
                  label="Hour"
                  items={TIME_HOURS}
                  format={String}
                  selected={parts ? hour12(parts.h) : null}
                  scrollTo={hour12(parts?.h ?? nowHour)}
                  active={false}
                  onPick={(h) => set(dayOr(day), timeOf(hour24(h, half), parts?.m ?? 0))}
                />
                <TimeList
                  label="Min"
                  items={TIME_MINUTES}
                  selected={slot}
                  scrollTo={slot ?? 0}
                  active={false}
                  onPick={(m) => {
                    const next = `${dayOr(day)} ${timeOf(parts?.h ?? nowHour, m)}`;
                    setText(next);
                    commit(next, null);
                  }}
                />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between border-t px-3 py-1.5 text-xs">
            <button
              type="button"
              className="text-primary font-medium hover:underline"
              onClick={() => {
                const now = momentText(momentAt(new Date()));
                setText(now);
                commit(now, null);
              }}
            >
              Now
            </button>
            <button
              type="button"
              className="text-primary hover:underline"
              onClick={() => {
                setText("");
                commit("", null);
              }}
            >
              Clear
            </button>
          </div>
        </Floating>
      )}
    </>
  );
}

/** One of the time picker's lists: the value highlighted, scrolled to it (or to `scrollTo`). */
function TimeList({
  label,
  items,
  format = pad2,
  selected,
  scrollTo,
  active,
  onPick,
}: {
  label: string;
  items: readonly number[];
  /** How an item reads: minutes 00–55, hours 12, 1–11. */
  format?: (n: number) => string;
  selected: number | null;
  scrollTo: number;
  /** ↑ ↓ step this list now. */
  active: boolean;
  onPick: (n: number) => void;
}) {
  const box = useRef<HTMLUListElement>(null);
  useLayoutEffect(() => {
    const el = box.current?.querySelector<HTMLElement>(`[data-value="${scrollTo}"]`);
    if (el && box.current) box.current.scrollTop = el.offsetTop - box.current.clientHeight / 2 + el.offsetHeight / 2;
  }, [scrollTo]);
  return (
    <div className={cn("flex-1 border-r last:border-r-0", active && "bg-muted/40")}>
      <div className="text-muted-foreground border-b px-2 py-1 text-center text-xs font-medium">{label}</div>
      <ul ref={box} role="listbox" aria-label={label} className="relative h-48 overflow-y-auto py-1 font-mono">
        {items.map((n) => (
          <li
            key={n}
            role="option"
            aria-selected={n === selected}
            data-value={n}
            className={cn(
              "mx-1 cursor-pointer rounded px-2 py-0.5 text-center",
              n === selected ? "bg-primary text-primary-foreground" : "hover:bg-muted",
              n === selected && active && "ring-primary/40 ring-2",
            )}
            onClick={() => onPick(n)}
          >
            {format(n)}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The time picker's AM / PM switch: the half shown, the other one a click away. */
function HalfSwitch({ half, onPick }: { half: TimeHalf; onPick: (h: TimeHalf) => void }) {
  return (
    <div className="flex gap-1 border-b p-1" role="radiogroup" aria-label="AM or PM" data-testid="time-half">
      {TIME_HALVES.map((h) => (
        <button
          key={h}
          type="button"
          role="radio"
          aria-checked={h === half}
          className={cn(
            "flex-1 rounded px-2 py-0.5 text-xs font-medium",
            h === half ? "bg-primary text-primary-foreground" : "hover:bg-muted",
          )}
          onClick={() => onPick(h)}
        >
          {h}
        </button>
      ))}
    </div>
  );
}

interface Choice {
  key: string;
  label: ReactNode;
  pick: (move: Move) => void;
}

/**
 * An input with choices under it: ↑ ↓ move the highlight, Enter / Tab pick it. Opened on a value,
 * that choice is highlighted (`initialActive`); typing moves the highlight to the first match.
 */
function ChoiceEditor({
  column,
  anchor,
  text,
  setText,
  choices,
  initialActive = 0,
  fallback,
  cancel,
  footer,
}: {
  column: ColumnDef;
  anchor: RefObject<HTMLElement | null>;
  text: string;
  setText: (t: string) => void;
  choices: Choice[];
  initialActive?: number;
  /** Enter with nothing highlighted. */
  fallback: (move: Move) => void;
  cancel: () => void;
  footer?: ReactNode;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [active, setActive] = useState(Math.max(0, initialActive));
  useEffect(() => ref.current?.focus(), []);
  // Typing moves the highlight to the first match — not the opening render.
  const opened = useRef(text);
  useEffect(() => {
    if (text !== opened.current) setActive(0);
  }, [text]);
  const pick = (move: Move) => (choices[active] ? choices[active].pick(move) : fallback(move));
  return (
    <>
      <input
        ref={ref}
        role="combobox"
        aria-expanded
        aria-label={column.header}
        className={inputClass}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={(e) => leftCell(e) && cancel()}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            e.stopPropagation();
            setActive((a) => Math.max(0, Math.min(choices.length - 1, a + (e.key === "ArrowDown" ? 1 : -1))));
            return;
          }
          const m = keyMove(e);
          if (m === undefined) return;
          e.preventDefault();
          e.stopPropagation();
          if (m === "cancel") cancel();
          else pick(m);
        }}
      />
      <Floating anchor={anchor}>
        <ul role="listbox" className="max-h-72 overflow-y-auto py-1">
          {choices.map((c, i) => (
            <li
              key={c.key}
              role="option"
              aria-selected={i === active}
              ref={(el) => {
                if (el && i === active) el.scrollIntoView({ block: "nearest" });
              }}
              className={cn(
                "flex cursor-pointer items-center justify-between gap-3 px-3 py-1.5",
                i === active && "bg-accent text-accent-foreground",
              )}
              onMouseEnter={() => setActive(i)}
              onClick={() => c.pick(null)}
            >
              {c.label}
            </li>
          ))}
        </ul>
        {footer ? <div className="text-muted-foreground border-t px-3 py-1.5 text-xs">{footer}</div> : null}
      </Floating>
    </>
  );
}

/** Routing status: the values of its list one may pick (`pickableStatuses`), each with what it counts as. */
export function SelectEditor(props: EditorProps) {
  const [text, setText] = useState("");
  const q = text.trim().toLowerCase();
  const choices = pickableStatuses(props.listValues)
    .filter((s) => !q || s.value.toLowerCase().includes(q))
    .map((s) => ({
      key: s.value,
      label: (
        <>
          <span className={cn("truncate", s.value === props.value && "font-semibold")}>{s.value}</span>
          {s.category !== s.value ? <span className="text-muted-foreground shrink-0 text-xs">{s.category}</span> : null}
        </>
      ),
      pick: (m: Move) => props.commit(s.value, m),
    }));
  return (
    <ChoiceEditor
      {...props}
      text={text}
      setText={setText}
      choices={choices}
      initialActive={choices.findIndex((c) => c.key === props.value)}
      fallback={() => props.cancel()}
    />
  );
}

/**
 * A list cell: opened, the whole list with the cell's value highlighted; typing searches it (most
 * used first), "Add … as a new value", "Clear the cell".
 */
export function ListEditor(props: EditorProps & { manager: boolean; onManage?: () => void }) {
  const { listValues, usage, value, commit, addValue } = props;
  const [text, setText] = useState(props.initial ?? "");
  const all = rankListValues(listValues, usage, text);
  const ranked = text.trim() ? all.slice(0, MAX_SHOWN) : all;
  const typed = text.trim();
  const exact = listValues.find((v) => v.retired !== "Yes" && v.value.toUpperCase() === typed.toUpperCase());
  const choices: Choice[] = ranked.map((v) => ({
    key: v.id,
    label: (
      <>
        <span className={cn("truncate", v.value === value && "font-semibold")}>{v.value}</span>
        <span className="text-muted-foreground shrink-0 text-xs tabular-nums">{usage.get(v.value) ?? 0}</span>
      </>
    ),
    pick: (m: Move) => commit(v.value, m),
  }));
  if (typed && !exact) {
    choices.push({
      key: "+add",
      label: <span className="text-primary">Add “{typed.toUpperCase()}” as a new value…</span>,
      pick: (m) => addValue(typed, m),
    });
  }
  if (value)
    choices.push({
      key: "+clear",
      label: <span className="text-muted-foreground">Clear the cell</span>,
      pick: (m) => commit("", m),
    });
  return (
    <ChoiceEditor
      {...props}
      text={text}
      setText={setText}
      choices={choices}
      initialActive={ranked.findIndex((v) => v.value === value)}
      fallback={(m) => (typed ? addValue(typed, m) : props.cancel())}
      footer={
        props.manager && props.onManage ? (
          <button type="button" className="text-primary hover:underline" onClick={props.onManage}>
            Manage this list (add · edit · retire · delete · merge)
          </button>
        ) : (
          "Anyone can add a value · edit, retire, delete or merge: managers only"
        )
      }
    />
  );
}

/**
 * Work Needed: several values of its list (`lists.ts`). Opened, the whole list with the cell's values
 * ticked; typing searches it (most used first). A click or Space ticks or unticks the highlighted
 * value; Enter with a search ticks its first match and clears the search, Enter with none saves (and
 * moves down), Tab saves and moves, Esc cancels, leaving the cell saves. "Add … as a new value" saves
 * it beside the ones ticked.
 */
export function MultiListEditor(props: EditorProps & { manager: boolean; onManage?: () => void }) {
  const { listValues, usage, value, commit, addValue, cancel, column, anchor } = props;
  const ref = useRef<HTMLInputElement>(null);
  const [text, setText] = useState(props.initial ?? "");
  const [chosen, setChosen] = useState(() => splitValues(value));
  const [active, setActive] = useState(0);
  useEffect(() => ref.current?.focus(), []);
  const typed = text.trim();
  const all = rankListValues(listValues, usage, text);
  // Ticked values first while nothing is typed, then the rest, most used first.
  const ranked = typed
    ? all.slice(0, MAX_SHOWN)
    : [...all].sort((a, b) => Number(!chosen.includes(a.value)) - Number(!chosen.includes(b.value)));
  const exact = listValues.find((v) => v.retired !== "Yes" && v.value.toUpperCase() === typed.toUpperCase());
  const toggle = (v: string) => setChosen((c) => (c.includes(v) ? c.filter((x) => x !== v) : [...c, v]));
  const save = (move: Move) => commit(joinValues(chosen), move);
  const rows = [
    ...ranked.map((v) => ({ key: v.id, value: v.value })),
    ...(typed && !exact ? [{ key: "+add", value: "" }] : []),
  ];
  const pick = (i: number, move: Move) => {
    const r = rows[i];
    if (!r) return;
    if (r.key === "+add") return addValue(typed, move, chosen);
    toggle(r.value);
  };
  return (
    <>
      <input
        ref={ref}
        role="combobox"
        aria-expanded
        aria-label={column.header}
        placeholder={chosen.length ? valuesText(joinValues(chosen)) : "Search"}
        className={inputClass}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setActive(0);
        }}
        onBlur={(e) => leftCell(e) && save(null)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            e.stopPropagation();
            setActive((a) => Math.max(0, Math.min(rows.length - 1, a + (e.key === "ArrowDown" ? 1 : -1))));
            return;
          }
          if (e.key === " " && !typed) {
            e.preventDefault();
            e.stopPropagation();
            pick(active, null);
            return;
          }
          const m = keyMove(e);
          if (m === undefined) return;
          e.preventDefault();
          e.stopPropagation();
          if (m === "cancel") return cancel();
          if (m === "down" && typed) {
            // Enter on a search: tick its match (or add it), then search again.
            if (rows[active]?.key === "+add") return addValue(typed, m, chosen);
            pick(active, null);
            setText("");
            return;
          }
          save(m);
        }}
      />
      <Floating anchor={anchor} width={Math.max(280, anchor.current?.offsetWidth ?? 0)}>
        <ul role="listbox" aria-multiselectable className="max-h-72 overflow-y-auto py-1">
          {rows.map((r, i) => (
            <li
              key={r.key}
              role="option"
              aria-selected={chosen.includes(r.value)}
              ref={(el) => {
                if (el && i === active) el.scrollIntoView({ block: "nearest" });
              }}
              className={cn(
                "flex cursor-pointer items-center gap-2 px-3 py-1.5",
                i === active && "bg-accent text-accent-foreground",
              )}
              onMouseEnter={() => setActive(i)}
              onClick={() => pick(i, null)}
            >
              {r.key === "+add" ? (
                <span className="text-primary">Add “{typed.toUpperCase()}” as a new value…</span>
              ) : (
                <>
                  <span
                    aria-hidden
                    className={cn(
                      "flex size-4 shrink-0 items-center justify-center rounded border text-[11px]",
                      chosen.includes(r.value) && "bg-primary border-primary text-primary-foreground",
                    )}
                  >
                    {chosen.includes(r.value) ? "✓" : ""}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{r.value}</span>
                  <span className="text-muted-foreground shrink-0 text-xs tabular-nums">{usage.get(r.value) ?? 0}</span>
                </>
              )}
            </li>
          ))}
        </ul>
        <div className="text-muted-foreground flex items-center justify-between gap-2 border-t px-3 py-1.5 text-xs">
          <span>{chosen.length ? `${chosen.length} ticked` : "None ticked"} · Space ticks · Enter saves</span>
          <span className="flex gap-2">
            {chosen.length ? (
              <button type="button" className="hover:underline" onClick={() => setChosen([])}>
                Clear all
              </button>
            ) : null}
            <button type="button" className="text-primary font-medium hover:underline" onClick={() => save(null)}>
              Save
            </button>
          </span>
        </div>
        {props.manager && props.onManage ? (
          <div className="border-t px-3 py-1.5 text-xs">
            <button type="button" className="text-primary hover:underline" onClick={props.onManage}>
              Manage this list (add · edit · retire · delete · merge)
            </button>
          </div>
        ) : null}
      </Floating>
    </>
  );
}

/**
 * Production Notes: free text, with the usual words offered. Opened on a value, every word with
 * the cell's highlighted; typing narrows them.
 */
export function SuggestEditor(props: EditorProps) {
  const [text, setText] = useState(props.initial ?? props.value);
  const narrowed = text !== props.value || props.initial !== undefined;
  const q = narrowed ? text.trim().toUpperCase() : "";
  const choices: Choice[] = [];
  const own = text.trim();
  if (own && !PRODUCTION_NOTE_SUGGESTIONS.some((s) => s === own.toUpperCase()))
    choices.push({ key: "+typed", label: <span>{own}</span>, pick: (m) => props.commit(text, m) });
  for (const s of PRODUCTION_NOTE_SUGGESTIONS) {
    if (s.includes(q) && (!narrowed || s !== q))
      choices.push({
        key: s,
        label: <span className={cn(s === props.value.toUpperCase() && "font-semibold")}>{s}</span>,
        pick: (m) => props.commit(s, m),
      });
  }
  if (props.value && !narrowed)
    choices.push({
      key: "+clear",
      label: <span className="text-muted-foreground">Clear the cell</span>,
      pick: (m) => props.commit("", m),
    });
  return (
    <ChoiceEditor
      {...props}
      text={text}
      setText={setText}
      choices={choices}
      initialActive={choices.findIndex((c) => c.key === props.value.toUpperCase() || c.key === "+typed")}
      fallback={(m) => props.commit(text, m)}
    />
  );
}
